import { useEffect, useState } from 'react'
import { Loader2, AlertCircle, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet'
import type { KeywordRanking, KeywordDetailData, PlacesSearchResult } from '../../types'
import { BRAND, CARTO_ATTRIBUTION, CARTO_TILE_URL } from '../../config'
import { scanApi } from '../../api/client'
import { KeywordGridHeatmap } from './KeywordGridHeatmap'

export const MAP_RESULTS_TOP = 5

interface KeywordRankingDetailProps {
  ranking: KeywordRanking
  scanId: string
  lang?: string
}

/** Numbered red circle marker for map pins. */
function makeNumberIcon(position: number) {
  return L.divIcon({
    html: `
      <div style="
        background:${BRAND.red};color:#fff;
        width:26px;height:26px;border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-size:12px;font-weight:700;
        border:2px solid #fff;
        box-shadow:0 2px 8px rgba(239,4,45,0.4);
      ">${position}</div>`,
    className: 'leaflet-custom-icon',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  })
}

/** Navy pin marking the business's own location. */
function makeBusinessIcon() {
  return L.divIcon({
    html: `
      <div style="
        background:${BRAND.navy};color:#fff;
        width:28px;height:28px;border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        border:2.5px solid #fff;
        box-shadow:0 2px 10px rgba(1,17,79,0.45);
      ">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>`,
    className: 'leaflet-custom-icon',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  })
}

function FitBounds({ points }: { points: L.LatLngTuple[] }) {
  const map = useMap()
  useEffect(() => {
    if (points.length === 0) return
    if (points.length === 1) {
      map.setView(points[0], 15)
      return
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 })
  }, [map, points])
  return null
}

interface RankingMapProps {
  results: PlacesSearchResult[]
  businessLat?: number
  businessLng?: number
}

function RankingMap({ results, businessLat, businessLng }: RankingMapProps) {
  const markers = results
    .filter(r => r.latitude != null && r.longitude != null && (r.latitude !== 0 || r.longitude !== 0))
    .slice(0, MAP_RESULTS_TOP)

  const points: L.LatLngTuple[] = markers.map(r => [r.latitude as number, r.longitude as number])
  const hasBusiness = businessLat != null && businessLng != null && (businessLat !== 0 || businessLng !== 0)
  if (hasBusiness) points.push([businessLat as number, businessLng as number])

  if (points.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#f2f2f6] rounded-2xl">
        <MapPin className="w-8 h-8 text-[#d1d5db]" />
      </div>
    )
  }

  return (
    <MapContainer
      center={points[0]}
      zoom={14}
      style={{ width: '100%', height: '100%' }}
      scrollWheelZoom={false}
    >
      <TileLayer
        url={CARTO_TILE_URL}
        attribution={CARTO_ATTRIBUTION}
      />
      {markers.map((r, i) => (
        <Marker
          key={r.place_id || i}
          position={[r.latitude as number, r.longitude as number]}
          icon={makeNumberIcon(r.position)}
        />
      ))}
      {hasBusiness && (
        <Marker position={[businessLat as number, businessLng as number]} icon={makeBusinessIcon()} zIndexOffset={1000} />
      )}
      <FitBounds points={points} />
    </MapContainer>
  )
}

function MapResultRow({ result }: { result: PlacesSearchResult }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
        style={{ backgroundColor: BRAND.red }}
      >
        {result.position}
      </div>
      <span className="flex-1 text-sm font-medium text-[#1e2939] truncate">{result.name}</span>
      {result.rating != null && (
        <span className="text-sm font-semibold text-[#d08700] shrink-0 tabular-nums">
          ⭐ {result.rating.toFixed(1)}
        </span>
      )}
    </div>
  )
}

export function KeywordRankingDetail({ ranking, scanId, lang = 'en' }: KeywordRankingDetailProps) {
  const { t } = useTranslation('report')
  const [detail, setDetail] = useState<KeywordDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await scanApi.getKeywordDetail(scanId, ranking.keyword, lang)
        setDetail(data)
      } catch {
        setError('Failed to load details')
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [ranking.keyword, scanId, lang])

  if (loading) {
    return (
      <div className="px-1 pb-1 pt-2">
        <div className="flex items-center justify-center gap-2 text-[#99a1af] py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">{t('rankings.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-1 pb-1 pt-2">
        <div className="flex items-center gap-2 text-red-600 text-sm py-4">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      </div>
    )
  }

  if (!detail) return null

  const mapResults = (detail.maps_results ?? []).slice(0, MAP_RESULTS_TOP)
  const searchResults = (detail.search_results ?? []).slice(0, 5)
  const isRanked = detail.is_ranked ?? ranking.is_ranked

  return (
    <div className="px-1 pb-1 pt-2">
      <div className="bg-[#f2f2f6] border border-[#d3d3d3] rounded-2xl p-4 sm:p-5 space-y-4">
        {/* Google Maps Results */}
        <div>
          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
            <h4 className="text-sm font-bold text-[#364153]">{t('rankings.mapsTitle')}</h4>
            <span className="text-xs text-[#4a5565]">{t('rankings.topMapResults')}</span>
          </div>
          <p className="text-xs text-[#4a5565] mb-3">{t('rankings.mapsSubtitle')}</p>

          <div className="bg-white border-2 border-[#d3d3d3] rounded-2xl p-3 sm:p-4 flex flex-col lg:flex-row gap-4">
            <div className="lg:w-[58%] shrink-0">
              <div className="ranking-map-root relative z-0 h-[180px] sm:h-[220px] w-full overflow-hidden rounded-2xl border border-[#e5e7eb]">
                <RankingMap
                  results={mapResults}
                  businessLat={detail.business_latitude}
                  businessLng={detail.business_longitude}
                />
              </div>
            </div>

            <div className="lg:flex-1 min-w-0 space-y-4 py-1">
              {mapResults.length === 0 ? (
                <p className="text-xs text-[#99a1af]">{t('rankings.noResults')}</p>
              ) : (
                mapResults.map((result, idx) => (
                  <MapResultRow key={result.place_id || idx} result={result} />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Local-visibility heatmap (geo-grid sampling) */}
        {ranking.grid_points && ranking.grid_points.length > 0 && (
          <KeywordGridHeatmap
            gridPoints={ranking.grid_points}
            averagePosition={ranking.average_position}
            coverage={ranking.coverage}
          />
        )}

        {/* Google Search Results */}
        <div className="bg-white border-2 border-[#d3d3d3] rounded-2xl p-4">
          <h4 className="text-sm font-bold text-[#364153] mb-2">{t('rankings.searchTitle')}</h4>
          <p className={`text-sm font-medium mb-4 ${isRanked ? 'text-[#008236]' : 'text-[#d08700]'}`}>
            {isRanked ? `✓ ${t('rankings.searchRanked')}` : `⚠ ${t('rankings.searchUnranked')}`}
          </p>

          {searchResults.length === 0 ? (
            <p className="text-xs text-[#99a1af]">{t('rankings.noResults')}</p>
          ) : (
            <div className="divide-y divide-[#e5e7eb]">
              {searchResults.map((result, idx) => (
                <div key={idx} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  {result.favicon_url ? (
                    <img
                      alt=""
                      className="mt-0.5 h-7 w-7 rounded-full border border-[#e5e7eb] bg-white p-1 shrink-0 object-contain"
                      src={result.favicon_url}
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden' }}
                    />
                  ) : (
                    <div className="mt-0.5 h-7 w-7 rounded-full border border-[#e5e7eb] bg-[#f9fafb] shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-[#99a1af] truncate">{result.domain}</div>
                    <div className="text-sm font-medium text-[#364153] leading-snug">{result.title}</div>
                    <div className="text-sm text-[#99a1af] leading-relaxed mt-0.5 line-clamp-2">{result.snippet}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
